import React, { useContext, useEffect, useState } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { FilterContext } from '../../../../../context/FilterProvider';
import CheckboxSelector from '../../../../checkboxSelector/checkboxSelector';
import ObrasAPI from '../../../../../services/APIObras';
import { Option } from '../../../../../types/Option';
import FILTER_TYPES from '../../../filterTypes';
import { filterMeaningfulOptions } from '../../../../../utils/filterMeaninglessOptions';

interface CustomCheckSelectorProps {
  filterType: string;
  t: TFunction<'translations'>;
}

const normalizeOptionName = (name: string): string => name
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const socialCategoryOrder = (name: string): number => {
  const normalizedName = normalizeOptionName(name);

  if (normalizedName.includes('alto') || normalizedName.includes('high')) return 0;
  if (normalizedName.includes('medio') || normalizedName.includes('medium')) return 1;
  if (normalizedName.includes('bajo') || normalizedName.includes('low')) return 2;

  return 3;
};

const gisPercentageValue = (name: string): number => Number.parseFloat(name.replace(',', '.'));

const CustomCheckSelector = ({ t, filterType }: CustomCheckSelectorProps) => {
  const { i18n } = useTranslation();
  const filterContext = useContext(FilterContext);
  const [options, setOptions] = useState<Array<Option>>([]);

  const NO_RESULTS: Option[] = [{ code: '', name: `${t('input.noResults')}` }];

  useEffect(() => {
    const api = ObrasAPI.getInstance();
    const filterTypeToApiFunction = {
      [FILTER_TYPES.projectStatus]: api.getContractStatus,
      [FILTER_TYPES.businessActivity]: api.getBusinessActivities,
      [FILTER_TYPES.spaceType]: api.getProtectedSpaces,
      [FILTER_TYPES.spaceLocation]: api.getProtectedSpacesLocations,
      [FILTER_TYPES.extinctionLevel]: api.getDangerLevels,
      [FILTER_TYPES.culturalLevel]: api.getProtectionZones,
      [FILTER_TYPES.typeContract]: api.getContractTypes,
      [FILTER_TYPES.modelContract]: api.getContractModels,
      [FILTER_TYPES.scopeContract]: api.getContractScope,
      [FILTER_TYPES.formPayment]: api.getFormPayment,
      [FILTER_TYPES.sourceFinancing]: api.getSourceFinance,
      [FILTER_TYPES.maturityLevel]: api.getMaturityLevel,
      [FILTER_TYPES.categorization]: api.getCategorization,
      [FILTER_TYPES.requirements]: api.getRequirements,
      [FILTER_TYPES.taxonomicActivity]: api.getTaxonomicObjectives,
      [FILTER_TYPES.socialCategory]: api.getSocialCategories,
      [FILTER_TYPES.gisPercentage]: api.getGISPercentage,
      [FILTER_TYPES.typesBimApplications]: api.getTypeBimApplicationsMasters,
    };
    const promise: Promise<Option[]> = filterTypeToApiFunction[filterType]
      ? filterTypeToApiFunction[filterType]() as Promise<Option[]>
      : api.getContractStatus();

    promise.then(
      (results) => {
        results = filterMeaningfulOptions(results);
        results = results.sort((a: Option, b: Option) => {
          if (filterType === FILTER_TYPES.socialCategory) {
            const socialOrderDifference = socialCategoryOrder(a.name || '') - socialCategoryOrder(b.name || '');
            if (socialOrderDifference !== 0) return socialOrderDifference;
          }

          if (filterType === FILTER_TYPES.gisPercentage) {
            const aValue = gisPercentageValue(a.name || '');
            const bValue = gisPercentageValue(b.name || '');
            if (Number.isFinite(aValue) && Number.isFinite(bValue) && aValue !== bValue) return bValue - aValue;
            if (Number.isFinite(aValue) !== Number.isFinite(bValue)) return Number.isFinite(aValue) ? -1 : 1;
          }

          return (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' });
        });
        results = results.length ? results : NO_RESULTS;
        setOptions(results);
      }
    ).catch((error) => {
      console.log(error);
      setOptions([]);
    });
  }, [i18n.language]);

  return (
    <>
      <CheckboxSelector
        filterType={filterType}
        options={options}
        filterContext={filterContext}
        placeholder={t('filters.filtersPlaceHolder.select') as string}
      />
    </>
  );
};

export default withTranslation()(CustomCheckSelector);
